// =====================================================
// BUDGET LINE EDITOR - Editor completo de línea
// Con soporte para odontograma, sociedades, tarifas
// FIX DESCUENTOS: selector desde catálogo del centro
// + badge visual en cabecera sin necesidad de expandir
// =====================================================
import { useState } from 'react';
import {
  Trash2,
  Copy,
  ChevronDown,
  ChevronUp,
  Building2,
  AlertTriangle,
} from 'lucide-react';
import { OdontogramInlineDisplay } from '../odontogram/OdontogramInlineDisplay';
import type { OdontogramSelection } from '../odontogram/OdontogramModal';
import type { DiscountSelect } from '../../api/discounts';
import { formatDiscountValue } from '../../api/discounts';

export interface EditableLine {
  _deleted?: boolean;
  tempId: string;
  id?: string;
  medicalActId: string;
  medicalActCode: string;
  medicalActName: string;
  requiresOdontogram: boolean;
  odontogramType: string | null;
  quantity: number;
  unitPrice: number;
  priceSource: string;
  discountType: 'PERCENTAGE' | 'FIXED' | null;
  discountValue: number | null;
  // Campos de descuento del catálogo
  discountId: string | null;
  discountName: string | null;
  odontogramSelection: OdontogramSelection | null;
  societyId: string | null;
  societyName: string | null;
  tariffId: string | null;
  tariffName: string | null;
  availableSocieties: Array<{ id: string; code: string; name: string; isDefault: boolean }>;
}

interface BudgetLineEditorProps {
  line: EditableLine;
  index: number;
  onUpdate: (updates: Partial<EditableLine>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onOpenOdontogram: () => void;
  calculateTotals: () => { subtotal: number; discountAmount: number; total: number };
  defaultSocietyId: string | null;
  patientAge: number;
  /** Descuentos activos vinculados al centro */
  availableDiscounts: DiscountSelect[];
}

export function BudgetLineEditor({
  line,
  index,
  onUpdate,
  onRemove,
  onDuplicate,
  onOpenOdontogram,
  calculateTotals,
  defaultSocietyId,
  patientAge,
  availableDiscounts,
}: BudgetLineEditorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [discountWarning, setDiscountWarning] = useState<{
    discount: DiscountSelect;
    overAmount: number;
  } | null>(null);
  const totals = calculateTotals();

  const hasDifferentSociety = line.societyId && line.societyId !== defaultSocietyId;
  const hasDiscount = !!(line.discountType && line.discountValue && line.discountValue > 0);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(price);

  // Al seleccionar un descuento del catálogo se autocompletan tipo y valor.
  // Si el descuento supera el subtotal de la línea se muestra un aviso de confirmación.
  const handleDiscountSelect = (discountId: string) => {
    setDiscountWarning(null);
    if (!discountId) {
      onUpdate({ discountId: null, discountName: null, discountType: null, discountValue: null });
      return;
    }
    const discount = availableDiscounts.find(d => d.id === discountId);
    if (!discount) return;

    const subtotal = line.quantity * line.unitPrice;
    const discountAmount = discount.type === 'PERCENTAGE'
      ? subtotal * (Number(discount.value) / 100)
      : Number(discount.value);

    if (discountAmount > subtotal) {
      // Guardar en estado de aviso — el usuario debe confirmar
      setDiscountWarning({ discount, overAmount: discountAmount - subtotal });
      return;
    }

    onUpdate({
      discountId: discount.id,
      discountName: discount.name,
      discountType: discount.type,
      discountValue: discount.value,
    });
  };

  const handleConfirmDiscountWarning = () => {
    if (!discountWarning) return;
    const { discount } = discountWarning;
    onUpdate({
      discountId: discount.id,
      discountName: discount.name,
      discountType: discount.type,
      discountValue: discount.value,
    });
    setDiscountWarning(null);
  };

  return (
    <div className={`
      border rounded-xl overflow-hidden transition-all
      ${hasDifferentSociety ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'}
    `}>
      {/* ── Cabecera de la línea ───────────────────── */}
      <div className={`flex flex-wrap items-center gap-2 md:gap-3 p-3 md:p-4 ${hasDifferentSociety ? 'bg-amber-50' : 'bg-gray-50'}`}>

        {/* Número */}
        <span className={`
          flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
          ${hasDifferentSociety ? 'bg-amber-200 text-amber-700' : 'bg-gray-200 text-gray-600'}
        `}>
          {index + 1}
        </span>

        {/* Info del acto */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-gray-500 bg-white px-1.5 py-0.5 rounded border">
              {line.medicalActCode}
            </span>
            <span className="font-medium text-gray-900 truncate">{line.medicalActName}</span>

            {/* Badge odontograma */}
            {line.requiresOdontogram && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full ${
                line.odontogramSelection ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
              }`}>
                🦷 {line.odontogramSelection ? 'Seleccionado' : 'Pendiente'}
              </span>
            )}

            {/* Badge sociedad diferente */}
            {hasDifferentSociety && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full">
                <Building2 className="h-3 w-3" />
                {line.societyName || 'Otra sociedad'}
              </span>
            )}

            {/* Badge descuento — visible SIN expandir */}
            {hasDiscount && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">
                <span className="material-symbols-outlined text-[12px]">percent</span>
                {line.discountName
                  ? `${line.discountName} (${formatDiscountValue(line.discountType!, line.discountValue!)})`
                  : formatDiscountValue(line.discountType!, line.discountValue!)}
              </span>
            )}
          </div>
        </div>

        {/* Cantidad + precio + acciones */}
        <div className="w-full md:w-auto md:contents">
          <div className="flex items-center gap-2">
            {/* Cantidad */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => line.quantity > 1 && onUpdate({ quantity: line.quantity - 1 })}
                className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-50 transition-colors"
                disabled={line.quantity <= 1}
              >−</button>
              <input
                type="number"
                value={line.quantity}
                onChange={(e) => onUpdate({ quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                className="w-12 h-7 text-center border border-gray-300 rounded text-sm font-medium"
                min={1}
              />
              <button
                onClick={() => onUpdate({ quantity: line.quantity + 1 })}
                className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100 text-gray-600 transition-colors"
              >+</button>
            </div>

            <span className="text-gray-400 font-light">×</span>

            {/* Precio unitario */}
            <div className="relative w-24">
              <input
                type="number"
                value={line.unitPrice}
                onChange={(e) => onUpdate({ unitPrice: parseFloat(e.target.value) || 0, priceSource: 'override' })}
                className="w-full h-7 px-2 text-right border border-gray-300 rounded text-sm font-medium"
                step="0.01"
                min={0}
              />
            </div>

            {/* Total línea */}
            <div className="w-24 text-right">
              <span className={`font-bold ${hasDiscount ? 'text-green-700' : 'text-gray-900'}`}>
                {formatPrice(totals.total)}
              </span>
            </div>

            {/* Acciones */}
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                title={isExpanded ? 'Contraer' : 'Expandir'}
              >
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              <button
                onClick={onDuplicate}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Duplicar"
              >
                <Copy className="h-4 w-4" />
              </button>
              <button
                onClick={onRemove}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Eliminar"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Odontograma inline */}
      {line.requiresOdontogram && line.odontogramSelection && !isExpanded && (
        <div className="px-4 pb-3 -mt-1">
          <OdontogramInlineDisplay
            selection={line.odontogramSelection}
            isChild={patientAge < 14}
            onEdit={onOpenOdontogram}
            compact
          />
        </div>
      )}

      {/* ── Panel expandido ────────────────────────── */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-200 space-y-4">

          {/* Odontograma */}
          {line.requiresOdontogram && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selección de odontograma
                {!line.odontogramSelection && (
                  <span className="ml-2 text-amber-600 text-xs font-normal">(pendiente)</span>
                )}
              </label>
              {line.odontogramSelection ? (
                <OdontogramInlineDisplay
                  selection={line.odontogramSelection}
                  isChild={patientAge < 14}
                  onEdit={onOpenOdontogram}
                />
              ) : (
                <button
                  onClick={onOpenOdontogram}
                  className="flex items-center gap-2 px-4 py-3 w-full border-2 border-dashed border-amber-300 bg-amber-50 rounded-xl text-amber-700 hover:bg-amber-100 hover:border-amber-400 transition-colors"
                >
                  <span className="text-xl">🦷</span>
                  <span className="font-medium">Seleccionar piezas dentales</span>
                  <AlertTriangle className="h-4 w-4 ml-auto" />
                </button>
              )}
            </div>
          )}

          {/* Sociedad y tarifa */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Sociedad
                {hasDifferentSociety && (
                  <span className="ml-2 text-amber-600 text-xs font-normal">(diferente al centro)</span>
                )}
              </label>
              <select
                value={line.societyId || ''}
                onChange={(e) => {
                  const selected = line.availableSocieties.find(s => s.id === e.target.value);
                  onUpdate({ societyId: e.target.value || null, societyName: selected?.name || null });
                }}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 ${
                  hasDifferentSociety ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
                }`}
              >
                <option value="">Sin sociedad específica</option>
                {line.availableSocieties.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.code} - {s.name}{s.isDefault ? ' (Por defecto)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Tarifa aplicada
              </label>
              <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-600">
                {line.tariffName || 'Precio base'}
              </div>
            </div>
          </div>

          {/* ── Descuento del catálogo ──────────────── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descuento en esta línea
            </label>

            {availableDiscounts.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <select
                    value={line.discountId || ''}
                    onChange={(e) => handleDiscountSelect(e.target.value)}
                    className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sin descuento</option>
                    {availableDiscounts.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.name} — {formatDiscountValue(d.type, d.value)}
                      </option>
                    ))}
                  </select>
                  {hasDiscount && (
                    <span className="text-sm font-semibold text-green-600">
                      -{formatPrice(totals.discountAmount)}
                    </span>
                  )}
                </div>
                {discountWarning && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                      <span className="text-amber-800">
                        El descuento <strong>{discountWarning.discount.name}</strong> supera el importe de la línea
                        en <strong>{formatPrice(discountWarning.overAmount)}</strong>.
                        El total de la línea quedará en <strong>0,00 €</strong>.
                      </span>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setDiscountWarning(null)}
                        className="px-3 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleConfirmDiscountWarning}
                        className="px-3 py-1 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
                      >
                        Aplicar de todas formas
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">
                No hay descuentos configurados para este centro
              </p>
            )}
          </div>

          {/* Resumen de la línea */}
          <div className="flex justify-end pt-3 border-t border-gray-100">
            <div className="text-sm space-y-1 min-w-[200px]">
              <div className="flex justify-between gap-8">
                <span className="text-gray-500">Subtotal:</span>
                <span className="font-medium">{formatPrice(totals.subtotal)}</span>
              </div>
              {totals.discountAmount > 0 && (
                <div className="flex justify-between gap-8">
                  <span className="text-gray-500">Descuento:</span>
                  <span className="text-green-600 font-medium">-{formatPrice(totals.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between gap-8 pt-2 border-t border-gray-200">
                <span className="font-semibold text-gray-700">Total línea:</span>
                <span className="font-bold text-gray-900">{formatPrice(totals.total)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
